import { useState } from "react";
import { Trash2, ArrowRightLeft, X, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../ui/alert-dialog";
import { DrillUsageNotice } from "./DrillUsageNotice";

// Super-admin bulk actions for one pool table: move every selected question to the other pool, or delete them.
// `onAction(action, ids, pool)` resolves when the server is done; the selection is cleared afterwards.
export const BulkActionBar = ({ pool, count, ids = [], onAction, onClear, busy }) => {
  const [confirm, setConfirm] = useState(null); // "delete" | "move"
  const isDaily = pool === "daily";
  const targetPool = isDaily ? "challenge" : "daily";
  const targetLabel = isDaily ? "Challenge Pool" : "Warm Up Pool";
  const noun = `${count} question${count === 1 ? "" : "s"}`;

  const run = async () => {
    const which = confirm;
    setConfirm(null);
    if (which === "delete") await onAction("delete", targetPool);
    else await onAction("move_pool", targetPool);
  };

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-xl border border-primary/30 bg-primary/5 px-3 py-2 mb-3" data-testid={`bulk-bar-${pool}`}>
      <span className="text-sm font-semibold" data-testid={`bulk-count-${pool}`}>{noun} selected</span>
      <Button size="sm" variant="outline" onClick={() => setConfirm("move")} disabled={busy} className="gap-1.5 rounded-full" data-testid={`bulk-move-${pool}-btn`}>
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRightLeft className="w-3.5 h-3.5" />} Move to {targetLabel}
      </Button>
      <Button size="sm" variant="outline" onClick={() => setConfirm("delete")} disabled={busy} className="gap-1.5 rounded-full border-destructive/50 text-destructive hover:bg-destructive/10" data-testid={`bulk-delete-${pool}-btn`}>
        <Trash2 className="w-3.5 h-3.5" /> Delete
      </Button>
      <Button size="sm" variant="ghost" onClick={onClear} disabled={busy} className="gap-1 rounded-full text-muted-foreground ml-auto" data-testid={`bulk-clear-${pool}-btn`}>
        <X className="w-3.5 h-3.5" /> Clear selection
      </Button>

      <AlertDialog open={!!confirm} onOpenChange={(v) => { if (!v) setConfirm(null); }}>
        <AlertDialogContent data-testid="bulk-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirm === "delete"
                ? `This permanently deletes ${noun} (and any schedules for them). This can't be undone.`
                : `This moves ${noun} to the ${targetLabel}. Scheduled dates are kept; each question joins the end of its new campaign order. Junior Blocks Ocean / Moon grids can't be warm-ups and will be skipped.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {confirm === "delete" && <DrillUsageNotice questionIds={ids} />}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={run} className={confirm === "delete" ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : ""} data-testid="bulk-confirm-ok">
              {confirm === "delete" ? `Delete ${noun}` : `Move ${noun}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
